/** Plain mic recorder (no music behind it) → a mono WAV File. Every mic failure opens the Microphone help dialog. */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { checkMicAvailability } from "@/lib/micCheck";
import { micTimeoutError, showMicHelp } from "@/lib/micHelp";
import { encodeWav } from "@/lib/wavEncoder";

const MIC_TIMEOUT_MS = 25000;
const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

async function toMonoWav(blob) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const src = await ctx.decodeAudioData(await blob.arrayBuffer());
    const mono = ctx.createBuffer(1, src.length, src.sampleRate);
    const out = mono.getChannelData(0);
    for (let c = 0; c < src.numberOfChannels; c += 1) {
      const ch = src.getChannelData(c);
      for (let i = 0; i < ch.length; i += 1) out[i] += ch[i] / src.numberOfChannels;
    }
    return encodeWav(mono);
  } finally { ctx.close(); }
}

/** Object URL for a File/Blob, revoked when it changes. */
export function useObjectUrl(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) { setUrl(null); return undefined; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

export default function useMicRecorder({ maxSec = 180, onRecorded }) {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const ref = useRef({});
  const doneRef = useRef(onRecorded);
  doneRef.current = onRecorded;

  const release = () => { clearInterval(ref.current.tick); ref.current.stream?.getTracks().forEach((t) => t.stop()); };
  useEffect(() => () => { ref.current.cancelled = true; if (ref.current.rec?.state === "recording") ref.current.rec.stop(); release(); }, []);

  const stop = useCallback(() => { if (ref.current.rec?.state === "recording") ref.current.rec.stop(); }, []);

  const start = useCallback(async () => {
    const pre = checkMicAvailability();
    if (pre) { showMicHelp(pre, () => start()); return; }
    setState("starting");
    let late = false;
    const ask = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
    ask.then((s) => { if (late) s.getTracks().forEach((t) => t.stop()); }, () => {});
    let stream;
    try {
      stream = await Promise.race([ask, new Promise((_, rej) => setTimeout(() => { late = true; rej(micTimeoutError()); }, MIC_TIMEOUT_MS))]);
    } catch (e) { setState("idle"); showMicHelp(e, () => start()); return; }
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = async () => {
      release();
      if (ref.current.cancelled) return;
      setState("saving");
      try {
        const wav = await toMonoWav(new Blob(chunks, { type: rec.mimeType || mime }));
        doneRef.current?.(new File([wav], `recording-${Date.now()}.wav`, { type: "audio/wav" }));
      } catch (e) { toast.error("That recording couldn't be read — please record again."); }
      finally { setState("idle"); setElapsed(0); }
    };
    const started = Date.now();
    ref.current = { stream, rec };
    rec.start(250);
    setElapsed(0);
    setState("recording");
    ref.current.tick = setInterval(() => {
      const s = (Date.now() - started) / 1000;
      setElapsed(s);
      if (s >= maxSec && rec.state === "recording") rec.stop();
    }, 200);
  }, [maxSec]);

  return { state, elapsed, start, stop };
}
